import { Empty } from 'antd';
import api from 'api';
import dayjs from 'dayjs';
import { IDataRoom, INotification } from 'interfaces';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { VariableSizeList } from 'react-window';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { fetchRoom } from 'store/reducers/roomsCreator';
import { fetchUserPermissions } from 'store/reducers/userDataCreator';
import { setCurrentFolder, setSelectedDocument, setViewType } from 'store/slices/dataDocumentsSlice';
import { setUserPermissions } from 'store/slices/dataPermissionsSlice';
import { setIsOpenConfrimInvite, setNotifications, setReplacedNotifications, setRoomToConfirm, setUnviewedNotifications } from 'store/slices/dataRoomSlice';
import { setRooms, setUserAndRoomStatus } from 'store/slices/userDataSlice';
import { setDocsSegment, setSelectedKeys } from 'store/slices/windowStateSlice';
import { AgreementNotificationsEnum, loadingNotificationEnum, SigningNotificationsEnum } from 'interfaces/INotification';
import NotificationRow from './NotificationRow';
import classes from './Notifications.module.scss';
import { agreementTypes, signingTypes } from './Notifications';
import { setSelectedChatRoom } from 'store/slices/chatsSlice';
import { getTheme } from 'helpers/getTheme';

type ActionsType = {
  onAccept: (event: any) => void;
  onReject: (event: any) => void;
};

type NotificationListProps = {
  setIsNotificationsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  notifications: INotification[];
  currentKey: string;
  filters?: string;
  setUpdatedNotifications: React.Dispatch<React.SetStateAction<string[]>>;
  updatedNotifications: string[]
};

function NotificationList({ setIsNotificationsOpen, notifications, currentKey, filters, setUpdatedNotifications, updatedNotifications}: NotificationListProps) {
  const [arrUpdatingIds, setArrUpdatingIds] = React.useState<string[]>([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = React.useState<boolean>(false);
  const [pagination, setPagination] = React.useState<any>({ pages: 0, current: 0, per_page: 40, total: 0 });
  const [currentNotifications, setCurrentNotifications] = React.useState<INotification[]>([]);

  const { userData, available_rooms } = useAppSelector((state) => state.userData);
  const { dataRoom, notificationPagination, notificationsUnviewed, notifications: allNotifications } = useAppSelector((state) => state.dataRoom);
  const { messageApi } = useAppSelector((state) => state.windowState);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';

  const notificationsWithCurrentTypes = currentKey === 'all' ? notifications : currentNotifications;

  const isFirstRenderState = React.useRef(true);
  const heightOfPopver = 400;
  const summheight = notificationsWithCurrentTypes.reduce((acc, cur) => acc += 110, 0);

  React.useEffect(() => {
    if (currentKey !== 'all') {
      notificationsUnviewed === 0 && setCurrentNotifications(prev => [...prev.map(notif => ({...notif, status: 1}))])
    }
  },[notificationsUnviewed]);

  React.useEffect(() => {
    if (currentKey === 'currentRoom' && dataRoom?.id && !isFirstRenderState.current) {
      getMoreNotification(`[{"field":"details.room_id","value":"${dataRoom?.id}"}]`)
    }
    return () => {
      if (currentKey === 'currentRoom') {
        setPagination({ pages: 0, current: 0, per_page: 40, total: 0 });
        setCurrentNotifications([]);
      }
    }
  },[dataRoom?.id]);

  React.useEffect(() => {
    if (currentKey !== 'all') {
      setCurrentNotifications(prev => [...prev.map(notification => (updatedNotifications.includes(notification.id)
        ? allNotifications.find(not => not.id === notification.id) || notification
        : notification
      ))]);
    }
  },[updatedNotifications]);

  React.useEffect(() => {    
    if (isFirstRenderState.current) {
      if (currentKey === 'all') {
        setPagination(notificationPagination);
      } else {
        setCurrentNotifications([{...notifications[0], type: loadingNotificationEnum.LOADING}]);
        getMoreNotification()
      }
      isFirstRenderState.current = false;
    }
  },[currentKey]);

  const getMoreNotification = async (forceFilters?: string) => {
    try {
      setIsLoadingNotifications(true);
      if (currentKey === 'all') {
        dispatch(setNotifications([...notificationsWithCurrentTypes, {...notificationsWithCurrentTypes[0], type: loadingNotificationEnum.LOADING}]));
        const newNotifications = await api.getNotificationsV2({
          locale,
          page: pagination.current + 1,
          per_page: pagination.per_page,
        });
  
        dispatch(setNotifications([...notificationsWithCurrentTypes, ...newNotifications.data.root]));
        setPagination(newNotifications.data.pagination);
      } else {
        !isFirstRenderState.current && setCurrentNotifications(prev => [...prev, {...prev[0], type: loadingNotificationEnum.LOADING}]);

        const newNotifications = await api.getNotificationsV2({
          locale,
          page: pagination.current + 1,
          per_page: pagination.per_page,
          filters: forceFilters ? forceFilters : filters,
        });        
        
        isFirstRenderState.current 
          ? setCurrentNotifications(newNotifications.data.root)
          : setCurrentNotifications([...notificationsWithCurrentTypes, ...newNotifications.data.root]);

        setPagination(newNotifications.data.pagination);
      }
    } catch {
      messageApi?.error(t('Header.Notification.messageApi?.errorGetNotifications'));
    } finally {
      setIsLoadingNotifications(false);
    };
  }
  
  const scrollHandler = React.useCallback((e: any) => {
    const currentPosition = e.scrollOffset + heightOfPopver;
    const isNeedToGetMoreNotifications = (notificationsWithCurrentTypes.length < pagination.total) && !isLoadingNotifications;
    
    if (summheight - currentPosition < 1000 && isNeedToGetMoreNotifications && !isFirstRenderState.current) {
      getMoreNotification();
    }
  }, [notificationsWithCurrentTypes, isLoadingNotifications, pagination]);

  const getSize = React.useCallback(
    (idx: number) => {
      return 116
      // return notificationsWithCurrentTypes[idx].details.invite_status === 0 ? 110 : 60;
    },
    [notificationsWithCurrentTypes, userData, available_rooms]
  );

  const cleanedPrevOpenFile = () => {
    dispatch(setSelectedDocument(null));
    dispatch(setViewType('Table'));
    dispatch(setDocsSegment('view'));
    dispatch(setSelectedChatRoom(null));
  };

  const updateNotificationStatus = async (receivedNotification: INotification) => {
    if (receivedNotification.status !== 1) {
      receivedNotification.status !== 1 && setArrUpdatingIds((prev) => [...prev, receivedNotification.id]);
      const { data } = await api.updateNotification({ notification_id: receivedNotification.id, status: 1 });
      setArrUpdatingIds(arrUpdatingIds.filter((id) => id === receivedNotification.id));
      setUpdatedNotifications(prev => [...prev, receivedNotification.id]);
      setCurrentNotifications(prev => [...prev.map(notification => (notification.id === data.id ? data : notification))]);
      dispatch(setUnviewedNotifications(notificationsUnviewed - 1));
      dispatch(setReplacedNotifications(
        notificationsWithCurrentTypes.map((notification) => (notification.id === data.id ? data : notification))
      ));
    };
  }

  if (!notificationsWithCurrentTypes[0]) return <Empty
    className={classes.noNotifications}
    image={Empty.PRESENTED_IMAGE_SIMPLE}
    description={t('Header.Notification.empty')}
  />;

  const VariableSizeListAny = VariableSizeList as any;

  return (
    <VariableSizeListAny
      itemCount={notificationsWithCurrentTypes.length}
      height={heightOfPopver}
      style={{ marginTop: -1, margin: '10px 2px 0 10px'}}
      className={getTheme()}
      itemSize={getSize}
      onScroll={scrollHandler}
      itemData={notificationsWithCurrentTypes}
      width={'calc(100% - 12px)'}
    >
      {({ index, style }: any) => {
        const onItemClick = async (file_id?: string) => {
          const currentN = notificationsWithCurrentTypes[index];
          cleanedPrevOpenFile();
          updateNotificationStatus(currentN);

          const notifType = currentN.type;

          let linkWithoutOrigin: string;

          if (currentN?.link?.includes('http')) {
            const u = new URL(String(currentN?.link));
            linkWithoutOrigin = currentN?.link?.replace(u.origin, '');
          } else {
            linkWithoutOrigin = currentN?.link || '';
          }
          
          const roomIdFromReg = (linkWithoutOrigin?.split('/') && linkWithoutOrigin?.split('/')[2]);
          const roomIdFromLink = roomIdFromReg
            ? roomIdFromReg
            : currentN.details?.room_id;

          const isGlobalChats = String(linkWithoutOrigin)?.includes('global-chats');
            
          const targetRoom = available_rooms.find(room => room.id === roomIdFromLink);
          // для уведомлений об инвайтах
          if (currentN.details?.invite_id){
            if (targetRoom && targetRoom?.invite_id) {
              targetRoom && dispatch(setIsOpenConfrimInvite(true));
              targetRoom && dispatch(setRoomToConfirm(targetRoom));
              setIsNotificationsOpen(false);
              return;
            } else if (!targetRoom) {
              setIsNotificationsOpen(false);
              return;
            }
          }
          
          if (currentN.link) {
            if (targetRoom?.user_status === 1 || targetRoom?.access_terms_status === 'waiting_for_approve') {
              navigate(`/room/${roomIdFromLink}/nda`);
            } else if (isGlobalChats) {
              navigate(currentN.link);
            } else if (!targetRoom) {
              messageApi?.error(t('main.inaccessibleRoom'), 6)
              setIsNotificationsOpen(false);
              return;
            } else {
              let correctPath = String(linkWithoutOrigin)
              let pathWithoutParams = correctPath.split('?')[0];
              
              if (
                [...agreementTypes, ...signingTypes].includes(notifType as AgreementNotificationsEnum | SigningNotificationsEnum) ||
                notifType.includes('status')
              ){
                if (!file_id) correctPath = pathWithoutParams;
              }  else if (file_id) {
                correctPath = pathWithoutParams + `?modalSection=view&document_id=${file_id}`;
              };
              navigate(correctPath);
            };
            
            const selectRoomTab = String(currentN.link).includes('prevTab=rooms');
            if (selectRoomTab) dispatch(setSelectedKeys(['rooms']));

            setIsNotificationsOpen(false);
            
            const regFolderId = currentN?.link?.match('/documents/(.*)/') || currentN?.link?.match('/documents/(.*)?mod');
            const folderId = currentN?.details?.folder_id || (regFolderId && regFolderId[1].replace('?', ''));

            if (!isGlobalChats) {
              if (!dataRoom && roomIdFromLink) {
                const errorMessage = () => messageApi?.error(t('main.inaccessibleRoom'), 10);
                await dispatch(fetchUserPermissions({ idRoom: roomIdFromLink }));
                await dispatch(fetchRoom({ id: roomIdFromLink, navigate, message: errorMessage }));
                dispatch(setUserAndRoomStatus('fulfilledDataRoom'));
              } else if (dataRoom?.id !== roomIdFromLink) {
                setTimeout(() => {
                  dispatch(setUserAndRoomStatus('fulfilledUserData'));
                  dispatch(setUserPermissions(null));
                }, 100);
              } else if (folderId) {
                const folder = await api.getFolderById(folderId, roomIdFromLink);
                const folderPath = folder.data.folder_path ? folder.data.folder_path?.reverse() : [];
                localStorage.setItem('previousFolderPath', JSON.stringify(folderPath));              
                dispatch(setCurrentFolder(folderPath));
              } else {
                dispatch(setCurrentFolder([]));
              }
            }
          };

        };

        const respToInvite: ActionsType = {
          onAccept: async () => {
            try {
              const response = await api.acceptInvite(notificationsWithCurrentTypes[index].details.invite_id!);
              const UTCDate = dayjs().valueOf() + new Date().getTimezoneOffset() * 60 * 1000;

              const newRooms: IDataRoom[] = available_rooms.map((room) =>
                room.id === response?.data?.id
                ? {
                    ...room,
                    invite_id: undefined,
                    agreement_enable: response.data.agreement_enable,
                    access_terms_status: response.data.access_terms_status,
                    last_action_timestamp: response.data.last_action_timestamp || UTCDate,
                    created_at: response.data.created_at,
                    lives_to: null,
                  }
                : room
              )
              dispatch(setRooms(newRooms));
              messageApi?.success(t('Header.Notification.invites.success.accepted'));
              dispatch(setReplacedNotifications(
                notificationsWithCurrentTypes.map((not, idx) =>
                  idx === index ? { ...not, details: { ...not.details, invite_status: 1 as any } } : not
                )
              ));
              setUpdatedNotifications(prev => [...prev, notificationsWithCurrentTypes[index].id]);
            } catch (Err) {
              messageApi?.error(t('Header.Notification.invites.error.accepted'));
            }
          },
          onReject: async () => {
            try {
              await api.declineInvite(notificationsWithCurrentTypes[index].details.invite_id!);
              messageApi?.success(t('Header.Notification.invites.success.declined'));
              dispatch(setReplacedNotifications(
                notificationsWithCurrentTypes.map((not, idx) =>
                  idx === index ? { ...not, details: { ...not.details, invite_status: 2 as any } } : not
                )
              ));
              setUpdatedNotifications(prev => [...prev, notificationsWithCurrentTypes[index].id]);
            } catch (Err) {
              messageApi?.error(t('Header.Notification.invites.error.declined'));
            }
          },
        };

        return (
          <NotificationRow
            dateFormat={userData?.date_format!}
            onItemClick={onItemClick}
            style={style}
            items={notificationsWithCurrentTypes}
            index={index}
            arrUpdatingIds={arrUpdatingIds}
            actions={respToInvite}
          />
        );
      }}
    </VariableSizeListAny>
  );
}

export default NotificationList;
